import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { getAccessToken, isSessionValid, clearSession } from '../services/authStorage';
import { getUserFromToken, getPermissionsFromToken, hasPermission as checkPermission } from '../utils/jwtUtils';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // Incrementado por refreshAuth() quando o token é trocado sem novo login
  // (ex.: edição de perfil), para reler os dados do usuário do JWT.
  const [version, setVersion] = useState(0);
  const refreshAuth = useCallback(() => setVersion((v) => v + 1), []);

  const value = useMemo(() => {
    const token = getAccessToken();
    const isValid = isSessionValid();

    if (!isValid || !token) {
      return {
        isAuthenticated: false,
        user: null,
        permissions: [],
        hasPermission: () => false,
        isPlatformAdmin: false,
        logout: clearSession,
        refreshAuth
      };
    }

    const user = getUserFromToken(token);
    const permissions = getPermissionsFromToken(token);

    return {
      isAuthenticated: true,
      user,
      permissions,
      hasPermission: (permission) => checkPermission(permissions, permission),
      isPlatformAdmin: user?.isPlatformAdmin === true,
      logout: clearSession,
      refreshAuth
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- version força a releitura do token
  }, [version, refreshAuth]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook para acessar autenticação e permissões
 * @returns {{
 *   isAuthenticated: boolean,
 *   user: {id, name, email, role} | null,
 *   permissions: string[],
 *   hasPermission: (permission: string) => boolean,
 *   logout: () => void,
 *   refreshAuth: () => void
 * }}
 */
export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }

  return context;
}